/**
 * MetricsActivationConversionPage — 数据中台 · 激活转化看板（ADM-MTR-S013）
 *
 * 以激活（首次归因安装，含 organic）为 cohort，看激活用户在观察期内是否走过
 * 进广告小说 → 进付费章 → 进付费面板 → 创建付费订单 → 付费，三个视图共用同一组筛选：
 * - 分组表格：任选分组维度 + 展示指标，首行为全窗口汇总，可排序、导出 CSV
 * - 时间趋势：按激活日折线，可再按一个维度拆分（取激活人数最多的 6 组）
 * - 转化链路：汇总六级 + 级间转化率
 * 数据走 useMetricsActivationConversion（GET /api/v1/metrics/activation-conversion，按激活 UTC 日）。
 * 应用维度跟随顶栏 App 选择器。
 */

import {
  ACTIVATION_CONVERSION_MAX_DAYS,
  type ActivationConversionDimension,
  earliestPaywallConversionDay,
} from '@novel-admin/shared';
import { useMemo, useState } from 'react';
import { useAppScope } from '~/app/app-scope-store';
import type {
  ActivationConversionMetricsView,
  ActivationConversionRowView,
  ActivationConversionViewData,
} from '~/data/adapters/metrics-activation-conversion-adapter';
import { useMetricsActivationConversion } from '~/data/queries';
import { fmtCountry, fmtLang } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { DateRangeButton } from '~/ui/DatePicker';
import { Dialog, DialogBody } from '~/ui/Dialog';
import { FilterButton } from '~/ui/FilterButton';
import { PageTabs } from '~/ui/PageTabs';
import { Select } from '~/ui/Select';
import { ChartSkeleton, TableSkeleton } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { LineChartX } from '~/ui/charts';
import {
  ChipRow,
  SERIES_COLORS,
  ToggleChip,
  daysBetween,
  defaultRange,
  downloadCsvFile,
  toDay,
} from './conversion-dashboard-shared';
import { MetricsDataTableHeading } from './metrics-data-explanation';
import { Cal, EmptyBlock, StateGate, type StateGateMode } from './metrics-shared';

type ViewTab = 'table' | 'trend' | 'chain';
type MetricKey = keyof ActivationConversionMetricsView;

type MetricDef = {
  key: MetricKey;
  label: string;
  group: '基础' | '比例' | '占比';
  kind: 'count' | 'pct';
  tip: string;
};

const METRICS: MetricDef[] = [
  {
    key: 'activations',
    label: '激活人数',
    group: '基础',
    kind: 'count',
    tip: '首次归因安装的用户（user_attributions，含自然量），按 App × 账号去重',
  },
  {
    key: 'adNovelUsers',
    label: '进广告小说用户数',
    group: '基础',
    kind: 'count',
    tip: '观察期内进入投放落点书籍阅读（chapter_view）的激活用户',
  },
  {
    key: 'paidChapterUsers',
    label: '进付费章用户数',
    group: '基础',
    kind: 'count',
    tip: '观察期内进入任意书籍付费章的激活用户（含需手动解锁、已解锁 / 畅读放行的付费章）',
  },
  {
    key: 'paywallUsers',
    label: '进付费面板用户数',
    group: '基础',
    kind: 'count',
    tip: '观察期内有 paywall_product_view 的激活用户',
  },
  {
    key: 'orderUsers',
    label: '创建付费订单用户数',
    group: '基础',
    kind: 'count',
    tip: '观察期内点击购买（store_purchase_attempt / Web 发起支付）的激活用户',
  },
  {
    key: 'paidUsers',
    label: '付费用户数',
    group: '基础',
    kind: 'count',
    tip: '观察期内有 Production 到账结算的激活用户',
  },
  {
    key: 'adNovelRate',
    label: '进广告小说用户比例',
    group: '比例',
    kind: 'pct',
    tip: '进广告小说用户数 ÷ 激活人数',
  },
  {
    key: 'paidChapterRate',
    label: '进付费章用户比例',
    group: '比例',
    kind: 'pct',
    tip: '进付费章用户数 ÷ 激活人数',
  },
  {
    key: 'paywallRate',
    label: '进付费面板用户比例',
    group: '比例',
    kind: 'pct',
    tip: '进付费面板用户数 ÷ 激活人数',
  },
  {
    key: 'orderRate',
    label: '创建付费订单用户比例',
    group: '比例',
    kind: 'pct',
    tip: '创建付费订单用户数 ÷ 激活人数',
  },
  {
    key: 'paidRate',
    label: '付费用户比例',
    group: '比例',
    kind: 'pct',
    tip: '付费用户数 ÷ 激活人数',
  },
  {
    key: 'paidChapterConversion',
    label: '进付费章用户转化率',
    group: '比例',
    kind: 'pct',
    tip: '进付费章用户数 ÷ 进广告小说用户数（自然量用户可不经广告书直接进付费章，可能 > 100%）',
  },
  {
    key: 'paywallConversion',
    label: '进付费面板用户转化率',
    group: '比例',
    kind: 'pct',
    tip: '进付费面板用户数 ÷ 进付费章用户数',
  },
  {
    key: 'orderConversion',
    label: '创建付费订单用户转化率',
    group: '比例',
    kind: 'pct',
    tip: '创建付费订单用户数 ÷ 进付费面板用户数',
  },
  {
    key: 'paidConversion',
    label: '付费用户转化率',
    group: '比例',
    kind: 'pct',
    tip: '付费用户数 ÷ 创建付费订单用户数',
  },
  {
    key: 'activationsShare',
    label: '激活人数占比',
    group: '占比',
    kind: 'pct',
    tip: '本行激活人数 ÷ 汇总行',
  },
  {
    key: 'adNovelShare',
    label: '进广告小说用户数占比',
    group: '占比',
    kind: 'pct',
    tip: '本行进广告小说用户数 ÷ 汇总行',
  },
  {
    key: 'paidChapterShare',
    label: '进付费章用户数占比',
    group: '占比',
    kind: 'pct',
    tip: '本行进付费章用户数 ÷ 汇总行',
  },
  {
    key: 'paywallShare',
    label: '进付费面板用户数占比',
    group: '占比',
    kind: 'pct',
    tip: '本行进付费面板用户数 ÷ 汇总行',
  },
  {
    key: 'orderShare',
    label: '创建付费订单用户数占比',
    group: '占比',
    kind: 'pct',
    tip: '本行创建付费订单用户数 ÷ 汇总行',
  },
  {
    key: 'paidShare',
    label: '付费用户数占比',
    group: '占比',
    kind: 'pct',
    tip: '本行付费用户数 ÷ 汇总行',
  },
];

const DEFAULT_METRICS: MetricKey[] = [
  'activations',
  'adNovelUsers',
  'adNovelRate',
  'paidChapterConversion',
  'paywallConversion',
  'orderConversion',
  'paidConversion',
];

const DIMENSIONS: {
  key: ActivationConversionDimension;
  label: string;
  group: '时间' | 'APP' | '广告' | '内容';
}[] = [
  { key: 'date', label: '日期', group: '时间' },
  { key: 'hour', label: '时间', group: '时间' },
  { key: 'app', label: '应用', group: 'APP' },
  { key: 'platform', label: '终端', group: 'APP' },
  { key: 'app_version', label: '版本', group: 'APP' },
  { key: 'channel', label: '渠道', group: '广告' },
  { key: 'country', label: '国家', group: '广告' },
  { key: 'ad_language', label: '投放语言', group: '广告' },
  { key: 'book_code', label: '书籍编码', group: '内容' },
  { key: 'book', label: '书籍名称', group: '内容' },
];

const PLATFORM_LABEL: Record<string, string> = { IOS: 'iOS', ANDROID: 'Android', WEB: 'Web' };
const CHANNEL_TYPE_LABEL: Record<string, string> = {
  NON_ORGANIC: '投放',
  ORGANIC: '自然量',
  ORGANIC_OR_UNKNOWN: '未知',
};
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const TREND_SERIES_LIMIT = 6;

const hourLabel = (hour: number | string) => `${String(hour).padStart(2, '0')}:00`;

function formatMetric(def: MetricDef, metrics: ActivationConversionMetricsView): string {
  const value = metrics[def.key];
  if (value === null) return '—';
  if (def.kind === 'pct') return `${(value * 100).toFixed(2)}%`;
  return value.toLocaleString();
}

function dimensionLabel(
  dimension: ActivationConversionDimension,
  value: string | null | undefined,
  data: ActivationConversionViewData,
): string {
  if (value === null || value === undefined || value === '') return '未知';
  switch (dimension) {
    case 'hour':
      return hourLabel(value);
    case 'app':
      return data.options.apps.find((app) => app.id === value)?.name ?? value;
    case 'platform':
      return PLATFORM_LABEL[value] ?? value;
    case 'channel':
      return value === 'organic' ? '自然量' : value;
    case 'country':
      return fmtCountry(value);
    case 'ad_language':
      return fmtLang(value);
    case 'book':
      return data.options.books.find((book) => book.id === value)?.title ?? value;
    default:
      return value;
  }
}

function downloadCsv(data: ActivationConversionViewData, metrics: MetricDef[]): void {
  const header = [
    ...data.groupBy.map((d) => DIMENSIONS.find((x) => x.key === d)?.label ?? d),
    ...metrics.map((m) => m.label),
  ];
  const line = (dims: string[], view: ActivationConversionMetricsView) => [
    ...dims,
    ...metrics.map((m) => formatMetric(m, view)),
  ];
  downloadCsvFile(`activation-conversion-${data.window.from}_${data.window.to}.csv`, [
    header,
    line(
      data.groupBy.map((_, index) => (index === 0 ? '汇总' : '')),
      data.summary,
    ),
    ...data.rows.map((row) =>
      line(
        data.groupBy.map((d) => dimensionLabel(d, row.dims[d], data)),
        row,
      ),
    ),
  ]);
}

export default function MetricsActivationConversionPage() {
  const appId = useAppScope((s) => s.appId);
  const [tab, setTab] = useState<ViewTab>('table');
  const [range, setRange] = useState<[Date, Date]>(defaultRange);
  const [hours, setHours] = useState<string[]>([]);
  const [channelTypes, setChannelTypes] = useState<string[]>([]);
  const [appVersions, setAppVersions] = useState<string[]>([]);
  const [novelIds, setNovelIds] = useState<string[]>([]);
  const [adLanguages, setAdLanguages] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [bookCodes, setBookCodes] = useState<string[]>([]);
  const [minActivationsInput, setMinActivationsInput] = useState('');
  const [tableDims, setTableDims] = useState<ActivationConversionDimension[]>(['date']);
  const [visibleMetrics, setVisibleMetrics] = useState<MetricKey[]>(DEFAULT_METRICS);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const [trendMetric, setTrendMetric] = useState<MetricKey>('activations');
  const [trendSplit, setTrendSplit] = useState<ActivationConversionDimension | ''>('');

  const from = toDay(range[0]);
  const to = toDay(range[1]);
  const spanDays = daysBetween(from, to).length;
  const earliestDay = earliestPaywallConversionDay(new Date());
  // 超出接口窗口上限，或起始日早于埋点保留期（转化各级恒 0）→ 不发请求、提示调整
  const rangeIssue =
    spanDays > ACTIVATION_CONVERSION_MAX_DAYS
      ? {
          title: `时间窗最多 ${ACTIVATION_CONVERSION_MAX_DAYS} 天`,
          note: `当前选择了 ${spanDays} 天，请缩短日期范围`,
        }
      : from < earliestDay
        ? {
            title: '起始日期超出埋点保留期',
            note: `埋点原始数据只保留 90 天，请从 ${earliestDay} 及之后开始选择`,
          }
        : null;
  const rangeBlocked = rangeIssue !== null;
  const minActivations = /^\d+$/.test(minActivationsInput.trim())
    ? Number(minActivationsInput.trim())
    : null;
  const groupBy: ActivationConversionDimension[] =
    tab === 'table' ? tableDims : tab === 'trend' && trendSplit ? ['date', trendSplit] : ['date'];

  const { data, isLoading, isFetching, isError, refetch } = useMetricsActivationConversion({
    from,
    to,
    groupBy,
    hours: hours.map(Number),
    appIds: appId === 'all' ? [] : [appId],
    channelTypes,
    appVersions,
    countries,
    adLanguages,
    bookCodes,
    novelIds,
    minActivations,
    enabled: !rangeBlocked,
  });

  const activeFilterCount =
    hours.length +
    channelTypes.length +
    appVersions.length +
    novelIds.length +
    adLanguages.length +
    countries.length +
    bookCodes.length +
    (minActivations !== null ? 1 : 0);
  const resetFilters = () => {
    setHours([]);
    setChannelTypes([]);
    setAppVersions([]);
    setNovelIds([]);
    setAdLanguages([]);
    setCountries([]);
    setBookCodes([]);
    setMinActivationsInput('');
  };

  const shownMetrics = METRICS.filter((m) => visibleMetrics.includes(m.key));
  const sortedRows = useMemo(() => {
    if (!data) return [];
    if (!sort) return data.rows;
    const factor = sort.dir === 'asc' ? 1 : -1;
    // 维度按展示文本排序：应用 / 书籍存的是 UUID、国家 / 语言是代码，按原值排会与屏幕上的顺序不符
    const sortValue = (row: ActivationConversionRowView): number | string | null => {
      if (!sort.key.startsWith('dim:')) return row[sort.key as MetricKey];
      const dimension = sort.key.slice(4) as ActivationConversionDimension;
      const raw = row.dims[dimension];
      return raw === null || raw === undefined ? null : dimensionLabel(dimension, raw, data);
    };
    return [...data.rows].sort((left, right) => {
      const a = sortValue(left);
      const b = sortValue(right);
      if (a === b) return 0;
      if (a === null) return 1;
      if (b === null) return -1;
      const order =
        typeof a === 'string' && typeof b === 'string' ? a.localeCompare(b) : a < b ? -1 : 1;
      return order * factor;
    });
  }, [data, sort]);

  const toggleSort = (key: string) =>
    setSort((current) =>
      current?.key !== key
        ? { key, dir: 'desc' }
        : current.dir === 'desc'
          ? { key, dir: 'asc' }
          : null,
    );

  const mode: StateGateMode = rangeBlocked
    ? 'normal'
    : isLoading
      ? 'loading'
      : isError || !data
        ? 'error'
        : 'normal';

  // 已选中但不在当前候选里的值也要能显示 / 取消（候选随时间窗变化）
  const withSelected = (options: string[], selected: string[]) => [
    ...new Set([...options, ...selected]),
  ];

  return (
    <div className="page" data-screen-label="激活转化看板">
      <div className="page-header">
        <div>
          <h1 className="page-title">激活转化看板</h1>
          <div className="page-sub hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
            激活 → 进广告小说 → 进付费章 → 进付费面板 → 创建付费订单 → 付费 · 按激活 UTC 日
            <Cal
              t="激活 cohort"
              title="激活 = 首次归因安装（含自然量）；各级只看激活时刻起至查询结束日次日 00:00 UTC（或当前时刻）的行为"
            />
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          // 重取期间 data 仍是上一组筛选的占位数据，禁止导出以免导出与当前筛选不符
          disabled={!data || isFetching || rangeBlocked || tab !== 'table'}
          title={tab === 'table' ? '导出当前分组表格' : '切到「分组表格」后导出'}
          onClick={() => {
            if (!data) return;
            downloadCsv(data, shownMetrics);
            toast('已导出 CSV', { tone: 'pos', icon: 'IconDownload' });
          }}
        >
          <Icons.IconDownload size={13} />
          导出
        </button>
      </div>

      <div
        className="card"
        style={{
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <span className="hstack" style={{ gap: 6, fontSize: 12, color: 'var(--fg-muted)' }}>
          <Icons.IconFilter size={13} />
          筛选
        </span>
        <DateRangeButton value={range} onChange={setRange} />
        <FilterButton
          label="小时"
          options={HOURS.map((hour) => ({ value: String(hour), label: hourLabel(hour) }))}
          value={hours}
          onChange={setHours}
        />
        <FilterButton
          label="渠道类型"
          options={Object.entries(CHANNEL_TYPE_LABEL).map(([value, label]) => ({ value, label }))}
          value={channelTypes}
          onChange={setChannelTypes}
        />
        <FilterButton
          label="版本"
          searchable
          options={withSelected(data?.options.app_versions ?? [], appVersions)}
          value={appVersions}
          onChange={setAppVersions}
        />
        <FilterButton
          label="书籍名称"
          searchable
          options={withSelected(
            (data?.options.books ?? []).map((book) => book.id),
            novelIds,
          ).map((id) => {
            const book = data?.options.books.find((b) => b.id === id);
            return { value: id, label: book ? `${book.title} · ${fmtLang(book.locale)}` : id };
          })}
          value={novelIds}
          onChange={setNovelIds}
        />
        <FilterButton
          label="投放语言"
          options={withSelected(data?.options.ad_languages ?? [], adLanguages).map((v) => ({
            value: v,
            label: fmtLang(v),
          }))}
          value={adLanguages}
          onChange={setAdLanguages}
        />
        <FilterButton
          label="国家"
          searchable
          options={withSelected(data?.options.countries ?? [], countries).map((v) => ({
            value: v,
            label: fmtCountry(v),
          }))}
          value={countries}
          onChange={setCountries}
        />
        <FilterButton
          label="书籍编码"
          searchable
          options={withSelected(data?.options.book_codes ?? [], bookCodes)}
          value={bookCodes}
          onChange={setBookCodes}
        />
        <label
          className="hstack"
          style={{ gap: 6, fontSize: 12, color: 'var(--fg-muted)' }}
          title="只显示激活人数不低于该值的分组行（不影响汇总行）"
        >
          激活人数 ≥
          <input
            className="input mono"
            inputMode="numeric"
            placeholder="不限"
            value={minActivationsInput}
            onChange={(e) => setMinActivationsInput(e.target.value.replace(/[^\d]/g, ''))}
            style={{ width: 80 }}
          />
        </label>
        <div className="hstack" style={{ gap: 8, marginLeft: 'auto' }}>
          {activeFilterCount > 0 && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={resetFilters}>
              <Icons.IconX size={11} />
              重置 {activeFilterCount}
            </button>
          )}
          {isFetching && !isLoading && (
            <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>更新中…</span>
          )}
        </div>
      </div>

      <PageTabs
        label="视图"
        value={tab}
        items={[
          { id: 'table', label: '分组表格' },
          { id: 'trend', label: '时间趋势' },
          { id: 'chain', label: '转化链路' },
        ]}
        onChange={setTab}
      />

      {tab === 'table' && (
        <div
          className="card"
          style={{
            padding: '12px 14px',
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            gap: '10px 24px',
          }}
        >
          <section className="vstack" style={{ gap: 8 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600 }}>分组维度</div>
            {(['时间', 'APP', '广告', '内容'] as const).map((group) => (
              <ChipRow key={group} label={group}>
                {DIMENSIONS.filter((d) => d.group === group).map((d) => (
                  <ToggleChip
                    key={d.key}
                    label={d.label}
                    on={tableDims.includes(d.key)}
                    onClick={() =>
                      setTableDims((current) =>
                        current.includes(d.key)
                          ? // 至少保留一个分组维度：空 group_by 在服务端回落为按日期，芯片状态会与表格不一致
                            current.length === 1
                            ? current
                            : current.filter((x) => x !== d.key)
                          : DIMENSIONS.map((x) => x.key).filter(
                              (x) => x === d.key || current.includes(x),
                            ),
                      )
                    }
                  />
                ))}
              </ChipRow>
            ))}
          </section>
          <section className="vstack" style={{ gap: 8 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600 }}>展示指标</div>
            {(['基础', '比例', '占比'] as const).map((group) => (
              <ChipRow key={group} label={group}>
                {METRICS.filter((m) => m.group === group).map((m) => (
                  <ToggleChip
                    key={m.key}
                    label={m.label}
                    title={m.tip}
                    on={visibleMetrics.includes(m.key)}
                    onClick={() =>
                      setVisibleMetrics((current) =>
                        current.includes(m.key)
                          ? current.filter((x) => x !== m.key)
                          : [...current, m.key],
                      )
                    }
                  />
                ))}
              </ChipRow>
            ))}
          </section>
        </div>
      )}

      <StateGate
        mode={mode}
        skeleton={tab === 'table' ? <TableSkeleton rows={10} cols={8} /> : <ChartSkeleton />}
        errorNote="GET /api/v1/metrics/activation-conversion 加载失败 · 请重试"
        onRetry={refetch}
      >
        {rangeIssue && <EmptyBlock title={rangeIssue.title} note={rangeIssue.note} />}
        {!rangeBlocked && data && tab === 'table' && (
          <GroupedTable
            data={data}
            rows={sortedRows}
            metrics={shownMetrics}
            sort={sort}
            onSort={toggleSort}
          />
        )}
        {!rangeBlocked && data && tab === 'trend' && (
          <TrendView
            data={data}
            metric={trendMetric}
            setMetric={setTrendMetric}
            split={trendSplit}
            setSplit={setTrendSplit}
            thresholdActive={minActivations !== null && minActivations > 1}
          />
        )}
        {!rangeBlocked && data && tab === 'chain' && <ChainView data={data} />}
      </StateGate>
    </div>
  );
}

function GroupedTable({
  data,
  rows,
  metrics,
  sort,
  onSort,
}: {
  data: ActivationConversionViewData;
  rows: ActivationConversionRowView[];
  metrics: MetricDef[];
  sort: { key: string; dir: 'asc' | 'desc' } | null;
  onSort: (key: string) => void;
}) {
  const arrow = (key: string) => (sort?.key === key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : '');
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <MetricsDataTableHeading title="激活转化明细" report="activation-conversion" />
      {data.truncated && (
        <div
          style={{
            padding: '8px 14px',
            fontSize: 12,
            color: 'var(--fg-muted)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          分组行超过上限，仅展示前 {rows.length.toLocaleString()} 行；请减少分组维度或缩小时间窗。
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <Table className="tbl" style={{ tableLayout: 'auto', minWidth: 720 }}>
          <thead>
            <tr>
              {data.groupBy.map((d) => (
                <th
                  key={d}
                  style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  onClick={() => onSort(`dim:${d}`)}
                >
                  {DIMENSIONS.find((x) => x.key === d)?.label}
                  {arrow(`dim:${d}`)}
                </th>
              ))}
              {metrics.map((m) => (
                <th
                  key={m.key}
                  className="num"
                  style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  onClick={() => onSort(m.key)}
                >
                  {m.label}
                  <MetricColumnHelp metric={m} />
                  {arrow(m.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr style={{ fontWeight: 600, background: 'var(--bg-subtle)' }}>
              {data.groupBy.map((d, index) => (
                <td key={d}>{index === 0 ? '汇总' : ''}</td>
              ))}
              {metrics.map((m) => (
                <td key={m.key} className="num mono">
                  {formatMetric(m, data.summary)}
                </td>
              ))}
            </tr>
            {rows.map((row) => (
              <tr key={row.key}>
                {data.groupBy.map((d) => (
                  <td
                    key={d}
                    className={
                      d === 'date' || d === 'hour' || d === 'app_version' || d === 'book_code'
                        ? 'mono'
                        : undefined
                    }
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {dimensionLabel(d, row.dims[d], data)}
                  </td>
                ))}
                {metrics.map((m) => (
                  <td key={m.key} className="num mono">
                    {formatMetric(m, row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      {rows.length === 0 && (
        <EmptyBlock title="当前筛选下没有分组数据" note="换个时间窗、放宽筛选或调低激活人数门槛" />
      )}
    </div>
  );
}

function MetricColumnHelp({ metric }: { metric: MetricDef }) {
  const [open, setOpen] = useState(false);
  const formula = metric.kind === 'pct';
  return (
    <>
      <button
        type="button"
        aria-label={`查看${metric.label}的${formula ? '计算公式' : '取值逻辑'}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 14,
          height: 14,
          marginLeft: 4,
          padding: 0,
          border: '1px solid var(--border-strong)',
          borderRadius: '50%',
          background: 'transparent',
          color: 'var(--fg-muted)',
          fontSize: 9,
          lineHeight: 1,
          cursor: 'pointer',
          verticalAlign: 'middle',
        }}
      >
        ?
      </button>
      {open && (
        <Dialog
          title={`${metric.label} · ${formula ? '计算公式' : '取值逻辑'}`}
          subtitle="当前表格列的数据统计口径"
          icon="IconInfo"
          width={480}
          onClose={() => setOpen(false)}
          footer={
            <button type="button" className="btn" onClick={() => setOpen(false)}>
              知道了
            </button>
          }
        >
          <DialogBody>
            <div className="card-flat" style={{ padding: 12, fontSize: 12.5, lineHeight: 1.7 }}>
              {formula && <b>计算公式</b>}
              <p
                className={formula ? 'mono' : undefined}
                style={{ margin: formula ? '6px 0 0' : 0 }}
              >
                {metric.tip}
              </p>
            </div>
          </DialogBody>
        </Dialog>
      )}
    </>
  );
}

function TrendView({
  data,
  metric,
  setMetric,
  split,
  setSplit,
  thresholdActive,
}: {
  data: ActivationConversionViewData;
  metric: MetricKey;
  setMetric: (metric: MetricKey) => void;
  split: ActivationConversionDimension | '';
  setSplit: (split: ActivationConversionDimension | '') => void;
  /** 激活人数门槛 > 1 时才可能过滤掉分组行（每个分组至少 1 人，门槛 0 / 1 不过滤任何行） */
  thresholdActive: boolean;
}) {
  const metricOptions = METRICS.filter((m) => m.group !== '占比');
  const def = METRICS.find((m) => m.key === metric) ?? METRICS[0];
  const days = daysBetween(data.window.from, data.window.to);
  const series = useMemo(() => {
    if (!def) return [];
    const groups = new Map<string, Map<string, ActivationConversionRowView>>();
    for (const row of data.rows) {
      const group = split ? (row.dims[split] ?? '') : '';
      const byDay = groups.get(group) ?? new Map<string, ActivationConversionRowView>();
      byDay.set(row.dims.date ?? '', row);
      groups.set(group, byDay);
    }
    // 拆分组按区间内激活规模排序，取前 N 组避免折线过密
    const ranked = [...groups.entries()]
      .map(([group, byDay]) => ({
        group,
        byDay,
        weight: [...byDay.values()].reduce((sum, row) => sum + row.activations, 0),
      }))
      .sort((left, right) => right.weight - left.weight)
      .slice(0, TREND_SERIES_LIMIT);
    return ranked.map(({ group, byDay }, index) => ({
      label: split ? dimensionLabel(split, group || null, data) : def.label,
      color: SERIES_COLORS[index % SERIES_COLORS.length] ?? 'var(--chart-1)',
      values: days.map((day) => {
        const row = byDay.get(day);
        // 门槛生效时缺行 = 该日被过滤（不是 0 人），画成断点而不是伪造的 0
        if (!row) return def.kind === 'count' && !thresholdActive ? 0 : null;
        const value = row[def.key];
        return value === null ? null : def.kind === 'pct' ? value * 100 : value;
      }),
    }));
  }, [data, split, def, days, thresholdActive]);

  const yFormat = (value: number) =>
    def?.kind === 'pct' ? `${value.toFixed(1)}%` : Math.round(value).toLocaleString();

  return (
    <div className="card vstack" style={{ padding: '12px 14px', gap: 10 }}>
      <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>指标</span>
        <Select
          value={metric}
          onChange={(e) => setMetric(e.target.value as MetricKey)}
          style={{ width: 200 }}
        >
          {metricOptions.map((m) => (
            <option key={m.key} value={m.key}>
              {m.label}
            </option>
          ))}
        </Select>
        <span style={{ fontSize: 12, color: 'var(--fg-muted)', marginLeft: 8 }}>拆分</span>
        <Select
          value={split}
          onChange={(e) => setSplit(e.target.value as ActivationConversionDimension | '')}
          style={{ width: 140 }}
        >
          <option value="">不拆分</option>
          {DIMENSIONS.filter((d) => d.key !== 'date').map((d) => (
            <option key={d.key} value={d.key}>
              {d.label}
            </option>
          ))}
        </Select>
        {split && (
          <span style={{ fontSize: 11.5, color: 'var(--fg-faint)' }}>
            按区间激活人数取前 {TREND_SERIES_LIMIT} 组
          </span>
        )}
      </div>
      {data.truncated ? (
        <EmptyBlock
          title="拆分后的分组超过上限，趋势不完整"
          note="服务端只返回了部分分组行，按此作图会漏掉组或把缺失日画成 0；请缩短时间窗或换一个取值更少的拆分维度"
        />
      ) : series.length === 0 || series.every((s) => s.values.every((v) => v === null)) ? (
        <EmptyBlock title="当前指标在所选拆分下无数据" note={def?.tip} />
      ) : (
        <LineChartX
          series={series}
          xLabels={days.map((day) => day.slice(5))}
          height={300}
          yFormat={yFormat}
        />
      )}
    </div>
  );
}

/**
 * 转化链路：各级是激活用户的子集但彼此不嵌套（自然量可不经广告书直接进付费章；埋点只覆盖同意分析采集
 * 的用户、付费覆盖全部用户），链路不保证单调递减。条宽按激活人数归一、级间转化率走 adapter 的零安全
 * 比率，上一级为 0 时不出 NaN / Infinity。
 */
function ChainView({ data }: { data: ActivationConversionViewData }) {
  const summary = data.summary;
  const steps: { label: string; value: number; conversion: number | null; hint: string }[] = [
    { label: '激活', value: summary.activations, conversion: null, hint: '' },
    {
      label: '进广告小说',
      value: summary.adNovelUsers,
      conversion: summary.adNovelRate,
      hint: '÷ 激活',
    },
    {
      label: '进付费章',
      value: summary.paidChapterUsers,
      conversion: summary.paidChapterConversion,
      hint: '÷ 进广告小说',
    },
    {
      label: '进付费面板',
      value: summary.paywallUsers,
      conversion: summary.paywallConversion,
      hint: '÷ 进付费章',
    },
    {
      label: '创建付费订单',
      value: summary.orderUsers,
      conversion: summary.orderConversion,
      hint: '÷ 进付费面板',
    },
    { label: '付费', value: summary.paidUsers, conversion: summary.paidConversion, hint: '÷ 下单' },
  ];
  const widest = Math.max(...steps.map((step) => step.value), 1);
  const pct = (value: number | null) => (value === null ? '—' : `${(value * 100).toFixed(2)}%`);
  return (
    <div className="card vstack" style={{ padding: '14px 16px', gap: 14 }}>
      <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
        {data.window.from} ~ {data.window.to} 激活用户（UTC 日），行为观察至{' '}
        <span className="mono">{data.observedUntil.slice(0, 16).replace('T', ' ')} UTC</span>
      </div>
      {summary.activations === 0 ? (
        <EmptyBlock title="时间窗内没有激活用户" note="换个时间窗或放宽筛选" />
      ) : (
        <div className="vstack" style={{ gap: 10 }}>
          {steps.map((step, index) => (
            <div key={step.label} className="vstack" style={{ gap: 4 }}>
              <div className="hstack" style={{ justifyContent: 'space-between', fontSize: 12.5 }}>
                <span>{step.label}</span>
                <span className="mono">
                  {step.value.toLocaleString()}
                  {index > 0 && (
                    <span style={{ color: 'var(--fg-muted)' }}>
                      {' '}
                      · {pct(step.conversion)} {step.hint}
                    </span>
                  )}
                </span>
              </div>
              <div style={{ height: 10, borderRadius: 5, background: 'var(--bg-inset)' }}>
                <div
                  style={{
                    width: `${(step.value / widest) * 100}%`,
                    height: '100%',
                    borderRadius: 5,
                    background: SERIES_COLORS[index] ?? 'var(--chart-1)',
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="hstack" style={{ gap: 24, flexWrap: 'wrap', fontSize: 12.5 }}>
        <span>
          整体付费率 <b className="mono">{pct(summary.paidRate)}</b>
        </span>
        <span>
          进付费章比例 <b className="mono">{pct(summary.paidChapterRate)}</b>
        </span>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--fg-faint)' }}>
        激活来自首次归因（覆盖全部完成归因上报的用户）；进广告小说 / 付费章 / 付费面板 /
        下单来自客户端埋点，只覆盖同意分析采集的用户；付费来自到账结算，覆盖全部用户。越近的激活日观察期越短，转化偏低。
      </div>
    </div>
  );
}
